import { useState, SyntheticEvent, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import { Box } from '@mui/system'
import {
  Stack,
  Tab,
  Typography,
  CircularProgress,
  Checkbox,
} from '@mui/material'
import { useQuery, useMutation, useQueryClient } from 'react-query'

import { usePageHeader } from '../../utils/hooks'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Button, Card, Input } from '../../components'
import {
  Container,
  MainSection,
  SettingsItemWrapper,
  SettingsRow,
  UpdateSection,
} from './SystemSettings.styled'
import { useSettingsService } from '../../services/settingsService'
import { TSettingsCategoryWithAttributes, TSettingBase } from '../../types'
import { RootState } from '../../store'
import { successToast } from '../../utils/toastService'
import { toast } from 'react-toastify'
import { EmptyState } from '../../components/ui/EmptyState/EmptyState'
import { HourglassEmpty } from '@mui/icons-material'

export const SystemSettings = () => {
  usePageHeader({
    headerTitle: 'System Settings',
  })

  const [searchParams, setSearchParams] = useSearchParams()
  const companyId = useSelector(
    (state: RootState) => state.user.profile?.company?.id
  )
  const { getSettings, updateSetting, bulkUpdateSettings } =
    useSettingsService()
  const queryClient = useQueryClient()

  const [localSettings, setLocalSettings] = useState<
    TSettingsCategoryWithAttributes[]
  >([])
  const [activeTab, setActiveTab] = useState<number | null>(null)

  useEffect(() => {
    if (localSettings.length > 0) {
      const tabParam = searchParams.get('tab')
      const validTabs = localSettings.filter((tab) => tab.attributes.length > 0)

      if (tabParam) {
        const tabId = parseInt(tabParam)
        const tabExists = validTabs.find((t) => t.id === tabId)
        if (tabExists) {
          setActiveTab(tabId)
          return
        }
      }

      if (!activeTab && validTabs.length > 0) {
        setActiveTab(validTabs[0].id)
      }
    }
  }, [localSettings])

  const { data, isLoading } = useQuery({
    queryKey: ['settings', companyId],
    queryFn: () => getSettings(Number(companyId)),
    enabled: !!companyId,
    onSuccess: (res) => {
      setLocalSettings(res.results)
      if (res.results.length > 0 && activeTab === null) {
        const tabParam = searchParams.get('tab')
        if (tabParam) {
          const tabId = parseInt(tabParam)
          const tabExists = res.results.find(
            (t) => t.id === tabId && t.attributes.length > 0
          )
          if (tabExists) {
            setActiveTab(tabId)
            return
          }
        }
        // Fallback to first valid tab
        const firstValidTab = res.results.find((t) => t.attributes.length > 0)
        if (firstValidTab) {
          setActiveTab(firstValidTab.id)
        }
      }
    },
    onError: (err?: { detail?: string }) => {
      const msg = err?.detail || 'Failed to load settings'
      toast.error(msg)
    },
    retry: false,
  })

  const mutation = useMutation({
    mutationFn: ({ id, value }: { id: number; value: string }) =>
      updateSetting(id, value),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', companyId] })
      successToast('Setting updated successfully.')
    },
  })

  const mutationBulkUpdate = useMutation({
    mutationFn: (updates: { id: number; value: string }[]) =>
      bulkUpdateSettings(updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', companyId] })
      queryClient.invalidateQueries({ queryKey: ['public-settings'] })
      successToast('All settings saved.')
    },
  })

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
    setSearchParams({ tab: newValue.toString() })
  }

  const handleValueChange = (
    categoryId: number,
    settingId: number,
    newValue: string
  ) => {
    setLocalSettings((prev) =>
      prev.map((category) =>
        category.id === categoryId
          ? {
              ...category,
              attributes: category.attributes.map((attr) =>
                attr.id === settingId ? { ...attr, value: newValue } : attr
              ),
            }
          : category
      )
    )
  }

  const handleSave = (setting: TSettingBase) => {
    mutation.mutate({ id: setting.id, value: setting.value })
  }

  const getDirtySettings = () => {
    return localSettings.flatMap((category) =>
      category.attributes
        .filter((attr) => {
          const originalAttr = data?.results
            .find((cat) => cat.id === category.id)
            ?.attributes.find((a) => a.id === attr.id)

          return originalAttr && originalAttr.value !== attr.value
        })
        .map((attr) => ({
          id: attr.id,
          value: attr.value,
        }))
    )
  }

  const saveAllSettings = () => {
    const updates = getDirtySettings()
    if (updates.length > 0) {
      mutationBulkUpdate.mutate(updates)
    }
  }

  return (
    <Container>
      <MainSection>
        {isLoading ? (
          <CircularProgress />
        ) : (
          <>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mb: 2,
              }}
            >
              <TabContext value={activeTab ?? 0}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <TabList
                    onChange={handleTabChange}
                    aria-label="Settings Tabs"
                  >
                    {localSettings.map((category) => (
                      <Tab
                        key={category.id}
                        label={category.label}
                        value={category.id}
                        disabled={category.attributes.length < 1}
                      />
                    ))}
                  </TabList>
                </Box>
              </TabContext>
            </Box>

            <TabContext value={activeTab ?? 0}>
              {localSettings.map((category) => (
                <TabPanel key={category.id} value={category.id}>
                  {category.attributes?.length < 1 ? (
                    <EmptyState
                      title="No settings available for this category"
                      icon={<HourglassEmpty />}
                    />
                  ) : (
                    <Stack gap={1}>
                      <SettingsRow
                        sx={{
                          padding: '0 4px',
                          color: (theme) => theme.palette.primary.main,
                          fontWeight: 'bold',
                        }}
                      >
                        <Typography width={240}>Key</Typography>
                        <Typography>Value</Typography>
                        <Typography width={100}></Typography>
                      </SettingsRow>
                      {category.attributes.map((attribute) => {
                        const originalAttr = data?.results
                          .find((cat) => cat.id === category.id)
                          ?.attributes.find((a) => a.id === attribute.id)

                        const isDirty = originalAttr?.value !== attribute.value

                        return (
                          <SettingsRow key={attribute.id}>
                            <SettingsItemWrapper
                              sx={{
                                ...(isDirty && {
                                  backgroundColor: '#fff9e5',
                                }),
                              }}
                            >
                              {attribute.label}
                            </SettingsItemWrapper>
                            <SettingsItemWrapper
                              sx={{
                                ...(isDirty && {
                                  backgroundColor: '#fff9e5',
                                }),
                              }}
                            >
                              {attribute.value_type === 'Boolean' ? (
                                <Checkbox
                                  checked={attribute.value === 'true'}
                                  onChange={(e) =>
                                    handleValueChange(
                                      category.id,
                                      attribute.id,
                                      e.target.checked ? 'true' : 'false'
                                    )
                                  }
                                />
                              ) : (
                                <Input
                                  variant="standard"
                                  value={attribute.value}
                                  onChange={(e) =>
                                    handleValueChange(
                                      category.id,
                                      attribute.id,
                                      e.target.value
                                    )
                                  }
                                />
                              )}
                            </SettingsItemWrapper>
                            {/*<SettingsItemWrapper*/}
                            {/*  sx={{ width: 100, backgroundColor: 'transparent' }}*/}
                            {/*>*/}
                            {/*  <Button*/}
                            {/*    variantType="text"*/}
                            {/*    sx={{*/}
                            {/*      width: 40,*/}
                            {/*      color: (theme) => theme.palette.primary.main,*/}
                            {/*    }}*/}
                            {/*    size="small"*/}
                            {/*    onClick={() => handleSave(attribute)}*/}
                            {/*    disabled={!isDirty || mutation.isLoading}*/}
                            {/*  >*/}
                            {/*    <Save color="inherit" />*/}
                            {/*  </Button>*/}
                            {/*</SettingsItemWrapper>*/}
                          </SettingsRow>
                        )
                      })}
                    </Stack>
                  )}
                </TabPanel>
              ))}
            </TabContext>
          </>
        )}
      </MainSection>
      <Card>
        <UpdateSection>
          <Button
            variant="contained"
            onClick={saveAllSettings}
            disabled={
              mutationBulkUpdate.isLoading || getDirtySettings().length === 0
            }
          >
            Save All Settings
          </Button>
        </UpdateSection>
      </Card>
    </Container>
  )
}
